import { pathOr } from 'ramda';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { PartThumbnail } from '@frontend/components';

import Label from 'modules/quotes/quoteItems/components/Label';

import { useClickPreventionOnDoubleClick } from 'utils/hooks';

import { quoteItemClicked } from './data/actions';

const AssemblyLabel = styled(Label)`
  font-family: 'Libre Franklin', Helvetica, Arial, sans-serif;
  font-size: 11px;
  font-weight: 500;
  margin-top: 3px;
  margin-bottom: 2px;
  padding: 3px 5px;
`;

const PartNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 6px 10px;
  word-break: break-word;
`;

const PartNameRow = styled.div`
  align-items: baseline;
  display: flex;
  line-height: 14px;
`;

const PrimaryLabelLeft = styled.p`
  color: #666666;
  flex-shrink: 0;
  font-family: 'Libre Franklin', Helvetica, Arial, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 16.5px;
  margin-right: 11px;
  min-width: 39px;
  text-align: right;
  text-transform: uppercase;
`;

const PrimaryLabelRight = styled.p`
  color: #222222;
  font-family: 'Roboto Mono', monospace;
  font-weight: 600;
  font-size: 12px;
  line-height: 16.5px;
  margin-right: 11px;
  word-break: break-all;
`;

const QuoteItemContainer = styled.div`
  align-items: stretch;
  display: flex;
  margin-top: -1px;
`;

const RightSide = styled.div`
  background: #fff;
  cursor: pointer;
  display: flex;
  flex-basis: 100%;
  transition: 0.2s ease background;

  &:hover {
    background: ${({ theme }) => theme.color.interaction.surfaceHover};
  }
  &:active {
    background: #ddd;
  }
`;

const SecondaryLabel = styled.p`
  color: #555555;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Roboto Mono', monospace;
  line-height: 16.5px;
  white-space: normal;
`;

const ThumbnailContainer = styled.div`
  align-items: center;
  background: linear-gradient(150deg, #fff 0%, #ccc 100%);
  border-right: solid 1px #eeeeee;
  display: flex;
  min-width: 55px;
  position: relative;
  overflow: hidden;
  width: 55px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: -0.46rem -0.75rem; /* the -0.46rem is to allow the borders on the table rows to peek out */
  min-width: 300px;
  user-select: none;
`;

const connector = connect(
  (state, { quoteItem, quoteItemId }) => ({
    quoteItem,
    quoteItemId,
  }),
  {
    onClick: quoteItemClicked,
  },
);

const QuoteItem = ({ onClick, quoteItem, quoteItemId }) => {
  const containsVirus = pathOr(
    false,
    ['rootComponent', 'part', 'containsVirus'],
    quoteItem,
  );
  const filename = pathOr('', ['rootComponent', 'part', 'filename'], quoteItem);
  const partNumber = pathOr('', ['rootComponent', 'partNumber'], quoteItem);
  const primaryFileUuid = pathOr(
    '',
    ['rootComponent', 'part', 'primaryFileUuid'],
    quoteItem,
  );
  const revision = pathOr('', ['rootComponent', 'revision'], quoteItem);
  const rootIsAssembly = pathOr('', ['rootComponent', 'isAssembly'], quoteItem);

  const handleClick = () => {
    if (onClick) {
      onClick({ quoteItemId });
    }
  };

  const [onExpandClick, onExpandDoubleClick] = useClickPreventionOnDoubleClick(
    handleClick,
    handleClick,
  );

  return (
    <Wrapper>
      <QuoteItemContainer>
        <RightSide onClick={onExpandClick} onDoubleClick={onExpandDoubleClick}>
          <ThumbnailContainer>
            <PartThumbnail
              containsVirus={containsVirus}
              filename={filename}
              partFileUuid={primaryFileUuid}
            />
          </ThumbnailContainer>
          <PartNameContainer>
            <PartNameRow>
              <PrimaryLabelLeft>Part #</PrimaryLabelLeft>
              <PrimaryLabelRight>{partNumber || '--'}</PrimaryLabelRight>
            </PartNameRow>
            <PartNameRow>
              <PrimaryLabelLeft>Rev</PrimaryLabelLeft>
              <PrimaryLabelRight>{revision || '--'}</PrimaryLabelRight>
            </PartNameRow>
            <PartNameRow>
              <SecondaryLabel>{filename}</SecondaryLabel>
            </PartNameRow>
            {rootIsAssembly && (
              <PartNameRow>
                <AssemblyLabel>Assembly</AssemblyLabel>
              </PartNameRow>
            )}
          </PartNameContainer>
        </RightSide>
      </QuoteItemContainer>
    </Wrapper>
  );
};

const ConnectedQuoteItem = connector(QuoteItem);

const renderQuoteItem = (quoteItemId, quoteItem) => {
  return <ConnectedQuoteItem quoteItemId={quoteItemId} quoteItem={quoteItem} />;
};

export default renderQuoteItem;
